import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import clsx from "clsx";
import {
  Dialog as HuiDialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { BellAlertIcon } from "@heroicons/react/24/outline";
import { Spinner } from "@/components/ui/Spinner";
import { Alert } from "@/components/ui/Alert";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { TodoQuickDialog } from "@/features/todos/TodoQuickDialog";
import { buildProjectTodoDraft } from "@/lib/todos";
import { useCurrentUser } from "@/lib/auth";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { buildProjectOverviewPanels } from "@/projectOverview/ProjectOverviewPanels";
import { useProjectOverviewData } from "@/projectOverview/useProjectOverviewData";
import {
  ensureOverviewGroups,
  userCanOpenProjectPage,
  userCanViewBueroProjectOverviewSections,
} from "@/projectOverview/permissions";
import { EditProjectDialog } from "@/projectOverview/EditProjectDialog";
import { DialogCloseButton } from "@/components/ui/dialogCloseGuard";
import { useTimesheetFlow } from "@/features/timesheetFlow/useTimesheetFlow";
import { useRealtime } from "@/lib/useRealtime";
import {
  realtimeEventConcernsOrder,
  useTrailingCallback,
} from "@/projectOverview/realtimeOrderEvent";
import { useInAppWindowFocus } from "@/lib/inAppWindowFocus";
import {
  MODAL_STACK_GROUP_PROJECT_OVERVIEW,
  ModalStackGroup,
  useModalStackLayer,
} from "@/lib/modalStack";
import { PROJECT_OVERVIEW_Z } from "@/projectOverview/projectOverviewLayers";
import type { ProjectOverviewReplyDraft } from "@/projectOverview/projectOverviewContextValue";
import { projectExecutionClassBadgeClass } from "@/lib/projects";
import { OrdnerJahrBadge } from "@/projectOverview/OrdnerJahrBadge";
import {
  auftragsartAppBadgeClass,
  resolveAuftragsartAppDisplay,
} from "@/pages/projekte/auftragsartApp";

type MobilePane = "details" | "notes";

export function ProjectOverviewDialog({
  open,
  orderId,
  highlightNoteId,
  replyDraft = null,
  openMontageunterlagen = false,
  stuecklisteId = null,
  onClose,
}: {
  open: boolean;
  orderId: number | null;
  highlightNoteId?: number | null;
  replyDraft?: ProjectOverviewReplyDraft | null;
  openMontageunterlagen?: boolean;
  stuecklisteId?: number | null;
  onClose: () => void;
}) {
  const { user } = useCurrentUser();
  const { focusSurface } = useInAppWindowFocus();
  const { zIndex: stackZIndex, bringToFront } = useModalStackLayer(
    open,
    MODAL_STACK_GROUP_PROJECT_OVERVIEW,
  );
  const { refreshKey } = useTimesheetFlow();
  const isMobile = useMediaQuery("(max-width: 767px)");
  const canOpenProjectPage = userCanOpenProjectPage(user);
  const canViewBueroSections = userCanViewBueroProjectOverviewSections(user);
  const { data, loading, error, refetch } = useProjectOverviewData(orderId, open, canViewBueroSections);
  const [groups, setGroups] = useState<string[]>([]);
  const [editOpen, setEditOpen] = useState(false);
  const [followUpOpen, setFollowUpOpen] = useState(false);
  const [mobilePane, setMobilePane] = useState<MobilePane>("details");
  const [isSwitchingProject, setIsSwitchingProject] = useState(false);
  const [stuecklisteEditorOpen, setStuecklisteEditorOpen] = useState(false);
  const prevOrderIdRef = useRef<number | null>(null);
  const stuecklisteEditorOpenRef = useRef(false);
  const pendingRefetchRef = useRef(false);
  stuecklisteEditorOpenRef.current = stuecklisteEditorOpen;

  const scheduleRefetch = useTrailingCallback(() => {
    void refetch();
  }, 250);

  const requestRefetch = useCallback(() => {
    if (stuecklisteEditorOpenRef.current) {
      pendingRefetchRef.current = true;
      return;
    }
    scheduleRefetch();
  }, [scheduleRefetch]);

  useEffect(() => {
    if (stuecklisteEditorOpen || !pendingRefetchRef.current) return;
    pendingRefetchRef.current = false;
    void refetch();
  }, [stuecklisteEditorOpen, refetch]);

  useEffect(() => {
    if (!open) {
      prevOrderIdRef.current = null;
      setIsSwitchingProject(false);
      setStuecklisteEditorOpen(false);
      pendingRefetchRef.current = false;
      return;
    }
    const prev = prevOrderIdRef.current;
    if (prev != null && orderId != null && prev !== orderId) {
      setIsSwitchingProject(true);
    }
    prevOrderIdRef.current = orderId;
  }, [open, orderId]);

  useEffect(() => {
    if (!isSwitchingProject || loading) return;
    if (data?.project.id === orderId) {
      const t = window.setTimeout(() => setIsSwitchingProject(false), 450);
      return () => window.clearTimeout(t);
    }
  }, [isSwitchingProject, loading, data?.project.id, orderId]);

  useEffect(() => {
    if (!open) return;
    void ensureOverviewGroups(user).then(setGroups);
  }, [open, user]);

  useEffect(() => {
    if (!open) {
      setMobilePane("details");
      return;
    }
    if (highlightNoteId || replyDraft) setMobilePane("notes");
  }, [open, highlightNoteId, replyDraft]);

  const seenRefreshKey = useRef<number | null>(null);
  useEffect(() => {
    if (!open || !orderId) {
      seenRefreshKey.current = null;
      return;
    }
    // Der Daten-Hook lädt beim Öffnen bereits. Hier nur nachziehen, wenn sich
    // Stundennachweise geändert haben – sonst startet jeder Öffnen-Vorgang
    // das Bundle doppelt und wartet auf den langsameren der beiden Läufe.
    if (seenRefreshKey.current === null) {
      seenRefreshKey.current = refreshKey;
      return;
    }
    if (seenRefreshKey.current === refreshKey) return;
    seenRefreshKey.current = refreshKey;
    void refetch();
  }, [open, orderId, refetch, refreshKey]);

  useEffect(() => {
    if (!open || !orderId) return;
    function onLocalChanged() {
      requestRefetch();
    }
    window.addEventListener("auftraege_changed", onLocalChanged);
    window.addEventListener("timesheets_changed", onLocalChanged);
    window.addEventListener("montageunterlagen_changed", onLocalChanged);
    window.addEventListener("orders_changed", onLocalChanged);
    window.addEventListener("material_anforderungen_changed", onLocalChanged);
    return () => {
      window.removeEventListener("auftraege_changed", onLocalChanged);
      window.removeEventListener("timesheets_changed", onLocalChanged);
      window.removeEventListener("montageunterlagen_changed", onLocalChanged);
      window.removeEventListener("orders_changed", onLocalChanged);
      window.removeEventListener("material_anforderungen_changed", onLocalChanged);
    };
  }, [open, orderId, requestRefetch]);

  useRealtime({
    scopes: [
      "auftraege:list",
      `auftraege:${orderId}`,
      `project:${orderId}`,
      `projects:${orderId}`,
    ],
    domains: [
      "auftraege",
      "projects",
      "hero",
      "timesheets",
      "orders",
      "material_anforderungen",
      "dokumente",
    ],
    events: [
      "auftraege_changed",
      "projects_changed",
      "hero_changed",
      "hero_custom_fields_updated",
      "timesheets_changed",
      "orders_changed",
      "material_anforderungen_changed",
      "dokumente_changed",
      "dokument_status_changed",
    ],
    enabled: open && !!orderId,
    onChange: (payload) => {
      if (!realtimeEventConcernsOrder(payload, orderId)) return;
      requestRefetch();
    },
  });

  const panels = useMemo(() => {
    if (!data || data.project.id !== orderId) return null;
    return buildProjectOverviewPanels({
      data,
      user,
      groups,
      onRefetch: requestRefetch,
      onEdit: () => setEditOpen(true),
      highlightNoteId,
      replyDraft,
      openMontageunterlagen,
      stuecklisteId,
      onStuecklisteEditorOpenChange: setStuecklisteEditorOpen,
    });
  }, [
    data,
    orderId,
    user,
    groups,
    requestRefetch,
    highlightNoteId,
    replyDraft,
    openMontageunterlagen,
    stuecklisteId,
  ]);

  const title = useMemo(() => {
    if (data?.project.id === orderId && data.project.auftragsnummer) {
      return `Projekt: ${data.project.auftragsnummer}`;
    }
    if (isSwitchingProject) return "Projekt wechseln …";
    if (loading) return "Lade Projekt …";
    return "Projekt";
  }, [data, orderId, isSwitchingProject, loading]);

  const executionClassBadge =
    data?.project.id === orderId
      ? String(data.project.execution_class || "").trim().toUpperCase()
      : "";
  const auftragsartBadge =
    data?.project.id === orderId
      ? resolveAuftragsartAppDisplay(data.project.auftragsart_app)
      : null;

  const showLoadingState =
    (loading && !panels) ||
    (Boolean(orderId) && data != null && data.project.id !== orderId);

  const canUseFollowUp = Boolean(user);
  const followUpProject =
    canUseFollowUp && orderId != null && data?.project.id === orderId
      ? data.project
      : null;

  return (
    <ModalStackGroup group={MODAL_STACK_GROUP_PROJECT_OVERVIEW}>
      <Transition show={open} as={Fragment}>
        <HuiDialog
          as="div"
          className={clsx("relative", PROJECT_OVERVIEW_Z)}
          style={stackZIndex != null ? { zIndex: stackZIndex } : undefined}
          onClose={onClose}
          onPointerDownCapture={() => {
            bringToFront();
            focusSurface("projectOverview");
          }}
        >
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-150"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" aria-hidden />
          </TransitionChild>

          <div
            className={clsx(
              "fixed inset-0",
              isMobile ? "overflow-hidden" : "overflow-y-auto",
            )}
          >
            <div
              className={clsx(
                "flex min-h-full items-center justify-center p-2 sm:p-4",
                isMobile && "h-full min-h-0 items-stretch p-0",
              )}
            >
              <TransitionChild
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 translate-y-2 scale-[0.99]"
                enterTo="opacity-100 translate-y-0 scale-100"
                leave="ease-in duration-100"
                leaveFrom="opacity-100 translate-y-0 scale-100"
                leaveTo="opacity-0 translate-y-2 scale-[0.99]"
              >
                <DialogPanel
                  className={clsx(
                    "eh-po-dialog w-full transform overflow-hidden bg-card shadow-ios-md transition",
                    isSwitchingProject && "eh-po-dialog--switching",
                    isMobile
                      ? "flex h-[100dvh] max-h-[100dvh] flex-col rounded-none"
                      : "rounded-ios-lg",
                  )}
                >
                  <div
                    className={clsx(
                      "flex shrink-0 items-center justify-between border-b border-separator px-4 py-3 sm:px-5",
                      isMobile &&
                        "pt-[max(0.75rem,env(safe-area-inset-top))]",
                      isSwitchingProject && "eh-po-dialog-header--switching",
                    )}
                  >
                    <DialogTitle className="min-w-0 flex-1 pr-3 text-base font-semibold text-label sm:text-lg">
                      <span
                        key={orderId ?? "none"}
                        className={clsx(
                          "eh-po-dialog-title flex min-w-0 items-center gap-2",
                          !isSwitchingProject && panels && "eh-po-dialog-title--enter",
                        )}
                      >
                        <span className="truncate">{title}</span>
                        {data?.project.id === orderId ? (
                          <OrdnerJahrBadge project={data.project} onMoved={requestRefetch} />
                        ) : null}
                        {auftragsartBadge?.kind ? (
                          <span
                            className={clsx(
                              "shrink-0",
                              auftragsartAppBadgeClass(auftragsartBadge.kind),
                            )}
                          >
                            {auftragsartBadge.label}
                          </span>
                        ) : null}
                        {executionClassBadge === "EXC2" ? (
                          <span
                            className={clsx(
                              "shrink-0 text-xs",
                              projectExecutionClassBadgeClass("EXC2"),
                            )}
                          >
                            EXC2
                          </span>
                        ) : null}
                      </span>
                    </DialogTitle>
                    <div className="flex shrink-0 items-center gap-2">
                      {followUpProject ? (
                        <button
                          type="button"
                          onClick={() => setFollowUpOpen(true)}
                          title="Projekt auf Wiedervorlage legen"
                          className="inline-flex h-8 items-center gap-1.5 rounded-ios bg-card px-2.5 text-sm font-medium text-label ring-1 ring-inset ring-separator-opaque hover:bg-fill-tertiary sm:px-3"
                        >
                          <BellAlertIcon className="h-4 w-4 text-accent" aria-hidden />
                          <span className="hidden sm:inline">Wiedervorlage</span>
                        </button>
                      ) : null}
                      {canOpenProjectPage && orderId ? (
                        <Link
                          to={`/projekte/${orderId}`}
                          onClick={onClose}
                          className="hidden h-8 items-center rounded-ios bg-card px-3 text-sm font-medium text-label ring-1 ring-inset ring-separator-opaque hover:bg-fill-tertiary sm:inline-flex"
                        >
                          Projektseite
                        </Link>
                      ) : null}
                      <DialogCloseButton onClick={onClose} />
                    </div>
                  </div>

                  {isMobile && panels && !showLoadingState ? (
                    <div className="shrink-0 border-b border-separator px-4 py-2.5">
                      <SegmentedControl
                        name="Projektübersicht Bereich"
                        value={mobilePane}
                        onChange={setMobilePane}
                        options={[
                          { value: "details", label: "Details" },
                          { value: "notes", label: "Notizen" },
                        ]}
                      />
                    </div>
                  ) : null}

                  <div
                    className={clsx(
                      "eh-po-dialog-body min-h-0 px-4 py-4 sm:px-5",
                      isMobile
                        ? "flex flex-1 flex-col overflow-hidden py-3"
                        : "max-h-[min(85vh,900px)]",
                    )}
                  >
                    {showLoadingState ? (
                      <div className="eh-po-dialog-switch-loading flex flex-1 items-center justify-center py-16">
                        <Spinner />
                        <span className="ml-2 text-sm text-label-secondary">
                          {isSwitchingProject ? "Wechsle Projekt …" : "Lade Projekt …"}
                        </span>
                      </div>
                    ) : error && !panels ? (
                      <Alert variant="error">{error}</Alert>
                    ) : panels ? (
                      isMobile ? (
                        <div
                          key={orderId}
                          className={clsx(
                            "eh-po-dialog-switch-content min-h-0 min-w-0 flex-1 flex flex-col overflow-hidden",
                          )}
                        >
                          {mobilePane === "details" ? (
                            <div className="eh-po-layout eh-po-layout--mobile min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
                              <div className="eh-po-main">{panels.main}</div>
                            </div>
                          ) : (
                            <div className="eh-po-layout eh-po-layout--mobile eh-po-layout--mobile-notes min-h-0 flex-1 overflow-hidden">
                              <div className="eh-po-notes">{panels.notes}</div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div
                          key={orderId}
                          className="eh-po-layout eh-po-layout--scroll-split eh-po-dialog-switch-content"
                        >
                          <div className="eh-po-main eh-po-main--scroll">{panels.main}</div>
                          <div className="eh-po-notes">{panels.notes}</div>
                        </div>
                      )
                    ) : null}
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </HuiDialog>
      </Transition>

      <EditProjectDialog
        open={editOpen}
        orderId={orderId}
        onClose={() => setEditOpen(false)}
        onSaved={() => void refetch()}
      />

      {followUpProject ? (
        <TodoQuickDialog
          open={followUpOpen}
          onClose={() => setFollowUpOpen(false)}
          title="Projekt auf Wiedervorlage legen"
          description={`Projekt ${followUpProject.auftragsnummer || `#${followUpProject.id}`} – Notiz ergänzen und Datum wählen.`}
          draft={buildProjectTodoDraft(followUpProject.id, followUpProject.auftragsnummer)}
          requireRemindAt
          successMessage="Wiedervorlage angelegt – erscheint auf der Startseite."
        />
      ) : null}
    </ModalStackGroup>
  );
}
